import { useTheme } from "@emotion/react"
import { Create } from "@mui/icons-material"
import { Box, Grid, Tooltip, Typography } from "@mui/material"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import React, { useEffect, useState } from "react"
import ShiftIcon from "assets/icons/shiftIcon.svg"
import Plus from "assets/icons/Plus.svg"
import trashIcon from "assets/icons/trash.svg"
import { Formik } from "formik"
import AddNewTileForm from "./component/addNewTileForm"
import { validationSchemaTilesForm } from "helpers/validationSchema"
import styles from "./tile.module.scss"
import CheckBox from "components/CheckBox"
import { useDispatch, useSelector } from "react-redux"
import {
  createTileAction,
  deleteTileAction,
  enableDisableTilesAction,
  getTileDetailsAction,
  getTilesListAction,
  reorderTilesAction,
  updateTileAction
} from "redux/superAdmin/actions"
import { useHistory } from "react-router-dom/cjs/react-router-dom.min"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"

const TileManagement = () => {
  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [isEdit, setIsEdit] = useState(false)
  const [addNewTileFormOpen, setAddNewTileOpen] = useState(false)
  const [selectedTiles, setSelectedTiles] = useState([])
  const [draggedIndex, setDraggedIndex] = useState()
  const [updatedTilesList, setUpdatedTilesList] = useState([])
  const [selectedTileId, setSelectedTileId] = useState("")
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const { tilesList, tileDetails } = useSelector((state) => state.superAdmin)
  const dispatch = useDispatch()
  const history = useHistory()
  const theme = useTheme()

  const handleCheckToggle = (id) => {
    setSelectedTiles((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  const isEvenSelection = selectedTiles.length % 2 === 0

  const initialValues = () => {
    if (isEdit) {
      return {
        tileName: tileDetails ? tileDetails?.name : "",
        link: tileDetails ? tileDetails?.link : ""
      }
    }

    return {
      tileName: "",
      link: ""
    }
  }

  const handleDragStart = (index) => setDraggedIndex(index)

  const handleDrop = (index) => {
    if (draggedIndex === null || draggedIndex === index) return
    const updatedOrder = [...updatedTilesList]
    const [draggedItem] = updatedOrder.splice(draggedIndex, 1)
    updatedOrder.splice(index, 0, draggedItem)
    const payload = {
      tile_order: updatedOrder?.map((item) => item?.id)
    }
    dispatch(reorderTilesAction(payload))
    setDraggedIndex(null)
  }

  const handleClose = () => {
    setAddNewTileOpen(false)
    if (isEdit) {
      setIsEdit(false)
    }
  }

  const handleEdit = (id) => {
    setIsEdit(true)
    setAddNewTileOpen(true)
    setSelectedTileId(id)
    dispatch(getTileDetailsAction(id))
  }

  const handleDelete = (id) => {
    setOpenDeleteDialog(true)
    setSelectedTileId(id)
  }

  const handleFormSubmit = (values) => {
    const payload = {
      name: values?.tileName,
      link: values?.link
    }
    if (isEdit) {
      dispatch(updateTileAction({ id: selectedTileId, payload, handleClose: () => handleClose() }))
    } else {
      dispatch(createTileAction({ payload, handleClose: () => handleClose() }))
    }
  }

  const handleSave = () => {
    const payload = {
      ids: selectedTiles,
      is_enabled: true
    }
    dispatch(
      enableDisableTilesAction({
        payload,
        handleRoute: () => history.push("/super-admin/dashboard")
      })
    )
  }

  useEffect(() => {
    setUpdatedTilesList(tilesList)
    setSelectedTiles(tilesList?.filter((item) => item?.is_enabled)?.map((item) => item?.id))
  }, [tilesList])

  useEffect(() => {
    document.title = "Tile Management"
    setBreadcrumbs(createBreadcrumbs())
    dispatch(getTilesListAction())
  }, [])

  return (
    <Grid container>
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onDelete={() => {
          dispatch(
            deleteTileAction({ id: selectedTileId, handleClose: () => setOpenDeleteDialog(false) })
          )
        }}
        title="Delete Tile"
        description="Are you sure you want to delete this tile?"
      />
      <Grid item xs={12}>
        <Header title="Tile Management" breadCrumbs={breadCrumbs}>
          <Tooltip
            title={
              selectedTiles.length === 0
                ? "Please select at least two categories"
                : !isEvenSelection
                ? "Please select even numbers of categories"
                : ""
            }
            disableHoverListener={selectedTiles.length > 0 && isEvenSelection}>
            <span>
              <AppButton
                className={styles.saveBtn}
                disabled={selectedTiles.length === 0 || !isEvenSelection}
                onClick={() => handleSave()}>
                Save
              </AppButton>
            </span>
          </Tooltip>
        </Header>
      </Grid>
      <Grid className="p-3" container item spacing={3}>
        <Grid item xs={6}>
          <Box className={styles.tileContaner}>
            <Box
              className={`d-flex align-items-center justify-content-between mb-4 ${styles.header}`}>
              <Typography variant="h3" color={theme.palette.text.secondary}>
                Select Category
              </Typography>
              <AppButton
                sx={{ marginLeft: "10px", padding: "9px 24px" }}
                onClick={() => {
                  setAddNewTileOpen(true)
                  setIsEdit(false)
                }}>
                <img src={Plus} alt="add" className="flex-shrink-0 me-1" />
                <Typography>Add New Tile</Typography>
              </AppButton>
            </Box>
            {updatedTilesList?.length === 0 ? (
              <Box
                className="d-flex justify-content-center align-items-center"
                sx={{ height: "80%" }}>
                <Typography variant="body1small">No tiles found</Typography>
              </Box>
            ) : (
              updatedTilesList?.map((item, index) => (
                <Box
                  className={styles.tile}
                  key={index}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}>
                  <Box className="d-flex align-items-center">
                    <CheckBox
                      checked={selectedTiles.includes(item?.id)}
                      color="secondary"
                      onClick={() => handleCheckToggle(item?.id)}
                      className={styles.checkBox}
                    />
                    <Typography variant="body1small">{item.name}</Typography>
                  </Box>
                  <Box className="d-flex align-items-center">
                    {item?.tile_type !== "default" && (
                      <>
                        <Create
                          className={styles.editIcon}
                          onClick={() => handleEdit(item?.id)}
                          color="secondary"
                        />
                        <img
                          className={styles.trashIcon}
                          src={trashIcon}
                          alt=""
                          onClick={() => handleDelete(item?.id)}
                        />
                      </>
                    )}
                    <img
                      src={ShiftIcon}
                      alt="shift"
                      className={styles.shiftIcon}
                      style={{
                        marginLeft: item?.tile_type !== "default" ? "8px" : 0
                      }}
                    />
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Grid>
        {(isEdit || addNewTileFormOpen) && (
          <Grid item xs={6}>
            <Box className={styles.newTileContainer}>
              <Typography variant="body1small">New Tile Details</Typography>
              <Formik
                initialValues={initialValues()}
                onSubmit={handleFormSubmit}
                validationSchema={validationSchemaTilesForm}
                enableReinitialize={true}>
                {(formik) => (
                  <AddNewTileForm formik={formik} isEdit={isEdit} onClose={() => handleClose()} />
                )}
              </Formik>
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default TileManagement
